import { inject, Injectable } from '@angular/core';
import {
    ITask2ReportAvailableItems,
    ITask2ReportItem,
    ITask2ReportWidget,
    ITask2ReportWidgetFilter,
} from '../fi-task-v2-report';
import { AppTextService } from '@fi-shared/app-text';
import {
    FiTaskOverviewTableColumns,
    FiTaskReportDateType,
    FiTaskReportStatusType,
    FiTaskReportType,
} from '../fi-task-v2-report-enums';

import { FiTaskReportStatuses } from '../../fi-task-v2-consts';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiTaskV2ReportItemOverviewDialogComponent } from '../fi-task-v2-report-item-overview-dialog/fi-task-v2-report-item-overview-dialog.component';
import { FiDialogsService } from '@fi-app/shared/fi-dialogs';
import { BehaviorSubject, Subject } from 'rxjs';
import { FiNewWidgetDialogComponent } from '../fi-new-widget-dialog/fi-new-widget-dialog.component';
import { IFiDialogResult } from '@fi-app/shared/fi-dialogs/fi-dialogs';
import { FiReportWidgetDialogsService } from '@fi-shared/fi-report-widget/fi-report-widget-dialogs.service';
import { INewWidgetDialogResult } from '@fi-shared/fi-report-widget/new-report-widget-dialog/new-widget-dialog-base';
import { FiTaskActionType, FiTaskAssignmentType, FiTaskType } from '@fi-app/task-v2/fi-task-v2-enums';
import { isDefined } from '@fi-shared/utils';
import { ACTIONS_TYPES } from '../fi-task-v2-report-widget-details/widget-details-content/widget-details-table/task-actions.builder.service';
import { Checkpoint2StatusType } from '@fi-app/shared/checklist/checklist-enum';
import { ITaskV2, ITaskV2ChecklistDataBase, ITaskV2Log } from '@fi-app/task-v2/fi-task-v2';
import { IDateByItem } from '@fi-shared/models/models';
import { IUserProfile } from '@fi-app/shared/profile-icon/profile-icon.component';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiTimeUnitType } from '@fi-shared/fi-format-date/fi-format-date-enums';
import { FiTaskStatusType } from '@fi-app/task-v2/fi-task-v2-enums';

const MSPerDay = 1000 * 60 * 60 * 24;

@Injectable()
export class FiTaskV2ReportHelperService {
    private readonly appTextService = inject(AppTextService);
    private readonly fiDialogsService = inject(FiDialogsService);
    private readonly fiReportWidgetDialogsService = inject(FiReportWidgetDialogsService);
    private readonly fiFormatDateService = inject(FiFormatDateService);

    readonly taskReportDateOptions: INameIdObj[] = [
        {
            Id: FiTaskReportDateType.PastWeek,
            Name: this.appTextService.get('common.last_week'),
        },
        {
            Id: FiTaskReportDateType.PastMonth,
            Name: this.appTextService.get('common.last_month'),
        },
        {
            Id: FiTaskReportDateType.PastQuarter,
            Name: this.appTextService.get('common.last_quarter'),
        },
        {
            Id: FiTaskReportDateType.PastYear,
            Name: this.appTextService.get('common.last_year'),
        },
    ];

    private _taskReportNameMap: Record<FiTaskReportType, string> = {
        [FiTaskReportType.All]: this.appTextService.get('task.all_tasks'),
        [FiTaskReportType.Overdue]: this.appTextService.get('task.report_name_overdue'),
        [FiTaskReportType.AssignedByMe]: this.appTextService.get('task.assigned_by_me'),
        [FiTaskReportType.ChecklistTasks]: this.appTextService.get('task.report_name_checklist'),
        [FiTaskReportType.AssignedToMe]: this.appTextService.get('task.assigned_to_me'),
        [FiTaskReportType.Private]: this.appTextService.get('task.private_tasks'),
    };
    private _taskReportDescriptionMap: Record<FiTaskReportType, string> = {
        [FiTaskReportType.All]: this.appTextService.get('task.all_tasks_description'),
        [FiTaskReportType.Overdue]: this.appTextService.get('task.report_name_overdue_description'),
        [FiTaskReportType.AssignedByMe]: this.appTextService.get('task.assigned_by_me_description'),
        [FiTaskReportType.ChecklistTasks]: this.appTextService.get('task.report_name_checklist_description'),
        [FiTaskReportType.AssignedToMe]: this.appTextService.get('task.assigned_to_me_description'),
        [FiTaskReportType.Private]: this.appTextService.get('task.private_tasks_description'),
    };
    private _taskReportIconClassMap: Record<FiTaskReportType, string> = {
        [FiTaskReportType.All]: 'far fa-clipboard-check',
        [FiTaskReportType.Overdue]: 'far fa-calendar-circle-exclamation',
        [FiTaskReportType.AssignedByMe]: 'far fa-user-pen',
        [FiTaskReportType.ChecklistTasks]: 'far fa-ballot-check',
        [FiTaskReportType.AssignedToMe]: 'far fa-user',
        [FiTaskReportType.Private]: 'far fa-user-lock',
    };

    readonly TaskReportOverviewDefaultVisibleColumns: FiTaskOverviewTableColumns[] = [
        FiTaskOverviewTableColumns.AssignedTo,
        FiTaskOverviewTableColumns.CreatedOn,
    ];

    isOverdueWidget(widget: ITask2ReportWidget): boolean {
        return widget.ReportType === FiTaskReportType.Overdue;
    }

    isCustomReportWidget(widget: ITask2ReportWidget): boolean {
        return !!widget.Name;
    }

    getTaskReportName(widget: ITask2ReportWidget): string {
        if (this.isCustomReportWidget(widget)) {
            return widget.Name;
        }

        if (widget.ReportType in this._taskReportNameMap) {
            return this.getTaskReportNameByReportType(widget.ReportType);
        }

        throw new Error(`unknown report type ${widget.ReportType} at getTaskReportName`);
    }

    getTaskReportNameByReportType(reportType: FiTaskReportType): string {
        return this._taskReportNameMap[reportType];
    }

    getTaskReportDescription(widget: ITask2ReportWidget): string {
        if (this.isCustomReportWidget(widget) && !!widget.Description?.length) {
            return widget.Description;
        }

        if (widget.ReportType in this._taskReportDescriptionMap) {
            return this._taskReportDescriptionMap[widget.ReportType];
        }

        throw new Error(`unknown report type ${widget.ReportType} at getTaskReportDescription`);
    }

    getTaskReportWidgetIconClass(widget: ITask2ReportWidget): string {
        if (widget.ReportType in this._taskReportIconClassMap) {
            return this.getTaskReportWidgetIconClassByReportType(widget.ReportType);
        }

        throw new Error(`unknown report type ${widget.ReportType} at getTaskReportWidgetIconClass`);
    }

    getTaskReportWidgetIconClassByReportType(reportType: FiTaskReportType): string {
        return this._taskReportIconClassMap[reportType];
    }

    getTaskReportStatusText(status: FiTaskReportStatusType): string {
        const textId = FiTaskReportStatuses.find((item) => item.id === status)?.textId;
        if (textId) {
            return this.appTextService.get(textId);
        }
        throw new Error(`unknown task report status ${status} at getTaskReportStatusText`);
    }

    getReportWidgetFilter(widget: ITask2ReportWidget): ITask2ReportWidgetFilter {
        let currentFilter: ITask2ReportWidgetFilter;
        if (widget.Settings.CurrentFilterGuid) {
            currentFilter = widget.Settings.Filters.find((item) => item.Guid === widget.Settings.CurrentFilterGuid);
        }
        return currentFilter ?? {};
    }

    openTaskViewerDialog(
        taskReport$: BehaviorSubject<ITask2ReportItem>,
        actionExecuted$: Subject<{ type: ACTIONS_TYPES; item: ITask2ReportItem }>,
    ): IFiDialogResult<void> {
        return this.fiDialogsService.custom({
            contentComponentRef: FiTaskV2ReportItemOverviewDialogComponent,
            contentComponentData: {
                taskReport$: taskReport$,
                actionExecuted$: actionExecuted$,
            },
            hideHeader: true,
            hideFooter: true,
            hideDialogTopBorder: true,
            config: {
                panelClass: ['fi-rounded-dialog-panel', 'fi-task-v2-report-item-overview-dialog-panel'],
                minWidth: '550px',
                maxWidth: '90vw',
                maxHeight: '90vh',
                minHeight: '400px',
                autoFocus: false,
            },
        });
    }

    private _openAddReportWidgetDialog$ = new Subject<void>();
    triggerOpenAddReportWidgetDialog(): void {
        this._openAddReportWidgetDialog$.next();
    }
    openAddReportWidgetDialog$ = this._openAddReportWidgetDialog$.asObservable();
    openAddReportWidgetDialog(): void {
        this.fiReportWidgetDialogsService
            .getNewWidgetTypeSelectDialog(this.availableTaskReportWidgets, true)
            .onYesButtonClick((reportType: FiTaskReportType) => {
                this.openWidgetSettingsDialog(reportType);
            });
    }

    private _saveReportWidget$ = new Subject<ITask2ReportWidget>();
    saveReportWidget$ = this._saveReportWidget$.asObservable();

    private openWidgetSettingsDialog(
        reportType: FiTaskReportType,
    ): IFiDialogResult<INewWidgetDialogResult<ITask2ReportWidget>> {
        return this.fiDialogsService.custom({
            contentComponentRef: FiNewWidgetDialogComponent,
            contentComponentData: reportType,
            hideHeader: true,
            hideFooter: true,
            hideDialogTopBorder: true,
            config: {
                panelClass: ['fi-rounded-dialog-panel', 'fi-new-widget-dialog-panel'],
                autoFocus: false,
                maxWidth: '90%',
                minWidth: '500px',
                minHeight: '200px',
                width: '1124px',
                restoreFocus: false,
            },
        }).onYesButtonClick((result) => {
            if (result.openTypeSelectStep) {
                this.openAddReportWidgetDialog();
                return;
            }
            this._saveReportWidget$.next(result.widget);
        });
    }

    readonly availableTaskReportWidgets: { IconClassName: string; Name: string; Id: FiTaskReportType }[] = [
        {
            IconClassName: this.getTaskReportWidgetIconClassByReportType(FiTaskReportType.All),
            Name: this.getTaskReportNameByReportType(FiTaskReportType.All),
            Id: FiTaskReportType.All,
        },
        {
            IconClassName: this.getTaskReportWidgetIconClassByReportType(FiTaskReportType.Overdue),
            Name: this.getTaskReportNameByReportType(FiTaskReportType.Overdue),
            Id: FiTaskReportType.Overdue,
        },
        {
            IconClassName: this.getTaskReportWidgetIconClassByReportType(FiTaskReportType.AssignedByMe),
            Name: this.getTaskReportNameByReportType(FiTaskReportType.AssignedByMe),
            Id: FiTaskReportType.AssignedByMe,
        },
        {
            IconClassName: this.getTaskReportWidgetIconClassByReportType(FiTaskReportType.ChecklistTasks),
            Name: this.getTaskReportNameByReportType(FiTaskReportType.ChecklistTasks),
            Id: FiTaskReportType.ChecklistTasks,
        },
        {
            IconClassName: this.getTaskReportWidgetIconClassByReportType(FiTaskReportType.AssignedToMe),
            Name: this.getTaskReportNameByReportType(FiTaskReportType.AssignedToMe),
            Id: FiTaskReportType.AssignedToMe,
        },
        {
            IconClassName: this.getTaskReportWidgetIconClassByReportType(FiTaskReportType.Private),
            Name: this.getTaskReportNameByReportType(FiTaskReportType.Private),
            Id: FiTaskReportType.Private,
        },
    ];

    getMergedAvailableItems(
        availableItemsBase: ITask2ReportAvailableItems,
        widgetAvailableItems: ITask2ReportAvailableItems,
    ): ITask2ReportAvailableItems {
        let mergedAvailableItems: ITask2ReportAvailableItems = Object.assign({}, availableItemsBase);
        if (widgetAvailableItems) {
            mergedAvailableItems.TaskNames = widgetAvailableItems.TaskNames;
        }
        return mergedAvailableItems;
    }

    getReportStatus(status: FiTaskStatusType, isDeadlinePassed: boolean): FiTaskReportStatusType {
        // copied from backend, should be synced in case of changes
        const reportStatus: FiTaskReportStatusType =
            status == FiTaskStatusType.Completed
                ? FiTaskReportStatusType.Completed
                : status == FiTaskStatusType.Cancelled
                  ? FiTaskReportStatusType.Cancelled
                  : isDeadlinePassed
                    ? FiTaskReportStatusType.Overdue
                    : status == FiTaskStatusType.NotStarted
                      ? FiTaskReportStatusType.NotStarted
                      : status == FiTaskStatusType.InProcess
                        ? FiTaskReportStatusType.InProgress
                        : undefined;

        if (!isDefined(reportStatus)) {
            throw new Error(`getReportStatus: unknown task status ${status}`);
        }

        return reportStatus;
    }

    getTaskReportOverviewBannerButtonText(hasViewAccessToChecklist: boolean, isChecklistAudit: boolean): string {
        // if user has rights to access/rights to view that specific iteration of the mentioned checklist/audit return text
        // otherwise return undefined;
        if (!hasViewAccessToChecklist) {
            return undefined;
        }

        return this.appTextService.get(isChecklistAudit ? 'checklist.view_audit' : 'checklist.view_checklist');
    }

    getTaskReportOverviewBannerText(checklistData: ITaskV2ChecklistDataBase): string {
        if (checklistData?.CheckpointAnswerStatus === Checkpoint2StatusType.NotOk) {
            return this.appTextService.get('task.report_automated_task_not_ok', {
                '{0}': checklistData?.UserFullName,
                '{1}': checklistData?.BusinessUnitName,
                '{2}': checklistData?.CheckpointName,
                '{3}': checklistData?.ChecklistName,
            });
        }
        return this.appTextService.get('task.report_automated_task_skipped', {
            '{0}': checklistData?.BusinessUnitName,
            '{1}': checklistData?.CheckpointName,
            '{2}': checklistData?.ChecklistName,
        });
    }

    isTaskInProgress(task: ITaskV2): boolean {
        return task.Status === FiTaskStatusType.InProcess;
    }

    isTaskCompleted(task: ITaskV2): boolean {
        return task.Status === FiTaskStatusType.Completed;
    }

    getTaskReportOverviewOverdueText(task: ITaskV2): string {
        const dateFrom: Date = new Date(task.Deadline);
        const dateTo: Date = this.isTaskCompleted(task) ? new Date(task.CompletedOn) : new Date();
        return this.fiFormatDateService.getDateDiffFullText(dateFrom, dateTo, {
            excludeTimeUnits: [FiTimeUnitType.Hour, FiTimeUnitType.Minute, FiTimeUnitType.Second],
            //includeZeroValues: true,
        });
    }

    isTaskCompletedAfterDeadline(task: { CompletedOn?: string; Deadline?: string; Status: FiTaskStatusType }): boolean {
        if (!task.CompletedOn || !task.Deadline) {
            return false;
        }

        const completedOnMs = new Date(task.CompletedOn).getTime();
        const deadlineMs = new Date(task.Deadline).getTime();

        // if task is completed in its overdue date - we should treat completion NOT after deadline
        // https://chainformation.atlassian.net/browse/FI-6838
        return task.Status === FiTaskStatusType.Completed && completedOnMs - deadlineMs >= MSPerDay;
    }
    private getLatestLogByType(latestTaskLogs: ITaskV2Log[], ...logTypes: FiTaskActionType[]): ITaskV2Log {
        const findFn =
            logTypes.length === 1
                ? (log: ITaskV2Log) => log.ActionType === logTypes[0]
                : (log: ITaskV2Log) => logTypes.includes(log.ActionType);
        return latestTaskLogs.find(findFn);
    }

    getLatestLogCompletedOnData(latestTaskLogs: ITaskV2Log[]): IDateByItem {
        const completedData = this.getLatestLogByType(latestTaskLogs, FiTaskActionType.SetCompleted);
        if (!completedData) {
            return undefined;
        }
        return this.convertCreatorToDateByItem(completedData);
    }

    getLatestLogStartedOnData(latestTaskLogs: ITaskV2Log[]): IDateByItem {
        const startedData = this.getLatestLogByType(latestTaskLogs, FiTaskActionType.Start);
        if (!startedData) {
            return undefined;
        }
        return this.convertCreatorToDateByItem(startedData);
    }

    getLatestLogArchivedOnData(latestTaskLogs: ITaskV2Log[]): IDateByItem {
        const archivedData = this.getLatestLogByType(latestTaskLogs, FiTaskActionType.Archive);
        if (!archivedData) {
            return undefined;
        }
        return this.convertCreatorToDateByItem(archivedData);
    }

    getLatestLogReopenedOnData(latestTaskLogs: ITaskV2Log[]): IDateByItem {
        const reopenedData = this.getLatestLogByType(
            latestTaskLogs,
            FiTaskActionType.Reopened,
            FiTaskActionType.ReopenedAndSetInProcess,
        );
        if (!reopenedData) {
            return undefined;
        }
        return this.convertCreatorToDateByItem(reopenedData);
    }

    convertCreatorToDateByItem(data: {
        CreatedOn?: string;
        CreatedByProfile?: IUserProfile;
        TaskType?: FiTaskType;
    }): IDateByItem {
        if (isDefined(data.TaskType) && data.TaskType === FiTaskType.ChecklistAutomated) {
            return {
                Date: data.CreatedOn,
                ItemProfile: { ...data.CreatedByProfile, Id: 0 },
            };
        }

        return {
            Date: data.CreatedOn,
            ItemProfile: data.CreatedByProfile,
        };
    }

    isTaskAssignedToUsersOnly(task: ITaskV2): boolean {
        return task.AssignmentType !== FiTaskAssignmentType.RolesUnits;
    }

    getTaskReportOverviewTableVisibleColumns(
        task: ITaskV2,
        latestTaskLogs: ITaskV2Log[],
        lastReminder: IDateByItem,
        isTaskOverdue: boolean,
    ): FiTaskOverviewTableColumns[] {
        const result: FiTaskOverviewTableColumns[] = [...this.TaskReportOverviewDefaultVisibleColumns];
        if (!this.isTaskAssignedToUsersOnly(task)) {
            result.push(FiTaskOverviewTableColumns.UsersAssignees);
        }

        const startedOnData = this.getLatestLogStartedOnData(latestTaskLogs);
        const reopenedLog = this.getLatestLogReopenedOnData(latestTaskLogs);
        const isStartedOnVisible =
            !!startedOnData && !reopenedLog && (this.isTaskInProgress(task) || this.isTaskCompleted(task));
        if (isStartedOnVisible) {
            result.push(FiTaskOverviewTableColumns.StartedOn);
        }

        if (!!reopenedLog) {
            result.push(FiTaskOverviewTableColumns.Reopened);
        }

        if (this.isTaskCompleted(task)) {
            result.push(FiTaskOverviewTableColumns.CompletedOn);
        }

        if (isTaskOverdue) {
            result.push(FiTaskOverviewTableColumns.Overdue);
        }

        if (lastReminder && !this.isTaskCompleted(task)) {
            result.push(FiTaskOverviewTableColumns.LastReminderDate);
        }

        if (task.IsArchived) {
            result.push(FiTaskOverviewTableColumns.ArchivedOn);
        }

        return result;
    }

    private readonly _columnMaxWidthPX = 300;
    private readonly _columnWidthDecreaseStepPX = 25;
    // use it in task-overview-table-wrapper.component.scss
    private readonly overviewTableColumnMaxWidthCSSVariableName = '--fi-task-overview-table-column-max-width';
    setTaskOverviewTableColumnMaxWidth(element: HTMLElement, visibleColumnsCount: number): void {
        const maxWidth = this._columnMaxWidthPX - this._columnWidthDecreaseStepPX * (visibleColumnsCount - 2);
        element.style.setProperty(this.overviewTableColumnMaxWidthCSSVariableName, `${maxWidth}px`);
    }

    private _currentTaskItem: ITask2ReportItem | undefined;
    setCurrentTaskItem(taskItem: ITask2ReportItem): void {
        this._currentTaskItem = taskItem;
    }
    getCurrentTaskItem(): ITask2ReportItem | undefined {
        return this._currentTaskItem;
    }
}
